import { useCallback, useEffect, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Link as MuiLink,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import {
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
  Home as HomeIcon,
  MoreVert as MoreVertIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { StudentFormFieldDialog } from '@/components/studentForm/StudentFormFieldDialog'
import { studentFormsApi, type StudentFormCatalogField } from '@/api/studentForms'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'
import { ROUTES } from '@/utils/constants'
import '@/styles/dashboard.css'

/**
 * Системные поля анкеты (админ) — единый каталог на всю платформу.
 *
 * Шаблонов анкет нет: этот список и есть анкета, а учитель лишь настраивает
 * её под себя (убирает поля, делает обязательными).
 */
export function AdminStudentFormFieldsPage() {
  const { t } = useTranslation()

  const [fields, setFields] = useState<StudentFormCatalogField[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [menuField, setMenuField] = useState<StudentFormCatalogField | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<StudentFormCatalogField | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setFields(await studentFormsApi.admin.list())
      setError(null)
    } catch (err) {
      setError(displayErrorMessage(getErrorMessage(err), t('studentForms.loadError')))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    void load()
  }, [load])

  const closeMenu = () => {
    setMenuAnchor(null)
    setMenuField(null)
  }

  const handleDelete = async (field: StudentFormCatalogField) => {
    closeMenu()
    if (!window.confirm(t('studentForms.deleteFieldConfirm'))) return
    try {
      await studentFormsApi.admin.remove(field.id)
      await load()
    } catch (err) {
      setError(displayErrorMessage(getErrorMessage(err), t('studentForms.deleteError')))
    }
  }

  /** Порядок задаётся списком идентификаторов целиком — меняем местами соседей. */
  const handleMove = async (index: number, direction: -1 | 1) => {
    const target = index + direction
    if (target < 0 || target >= fields.length) return

    const ids = fields.map((field) => field.id)
    ;[ids[index], ids[target]] = [ids[target], ids[index]]

    try {
      setFields(await studentFormsApi.admin.reorder(ids))
    } catch (err) {
      setError(displayErrorMessage(getErrorMessage(err), t('studentForms.saveFieldError')))
    }
  }

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{ mb: 2 }}
        >
          <MuiLink
            component={RouterLink}
            to={ROUTES.DASHBOARD}
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('dashboard.home')}
          </MuiLink>
          <Typography color="text.primary">{t('studentForms.catalogTitle')}</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Typography variant="h4" component="h1" fontWeight={700}>
            {t('studentForms.catalogTitle')}
          </Typography>
          <PageHelpPopover pageKey="admin-student-form-fields" />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 240 }}>
            {t('studentForms.catalogHint')}
          </Typography>
          <Button
            size="small"
            variant="contained"
            sx={{ height: 40 }}
            onClick={() => {
              setEditing(null)
              setDialogOpen(true)
            }}
          >
            {t('studentForms.createField')}
          </Button>
        </Box>

        <Card>
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            {loading ? (
              <Box sx={{ p: 2 }}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} variant="rectangular" height={52} sx={{ mb: 1, borderRadius: 1 }} />
                ))}
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('studentForms.fieldLabel')}</TableCell>
                      <TableCell>{t('studentForms.fieldKey')}</TableCell>
                      <TableCell>{t('studentForms.fieldType')}</TableCell>
                      <TableCell>{t('studentForms.fieldAccess')}</TableCell>
                      <TableCell>{t('studentForms.fieldFlags')}</TableCell>
                      <TableCell align="right">{t('common.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fields.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          {t('studentForms.noFields')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      fields.map((field, index) => (
                        <TableRow key={field.id} hover>
                          <TableCell>{field.label}</TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {field.key}
                            </Typography>
                          </TableCell>
                          <TableCell>{t(`studentForms.fieldTypes.${field.type}`)}</TableCell>
                          <TableCell>{t(`studentForms.accessLevels.${field.access}`)}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {field.required && (
                                <Chip
                                  size="small"
                                  color="primary"
                                  label={t('studentForms.fieldRequired')}
                                />
                              )}
                              {!field.deletable && (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={t('studentForms.fieldNotDeletable')}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              disabled={index === 0}
                              onClick={() => void handleMove(index, -1)}
                            >
                              <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              disabled={index === fields.length - 1}
                              onClick={() => void handleMove(index, 1)}
                            >
                              <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                setMenuAnchor(e.currentTarget)
                                setMenuField(field)
                              }}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
          <MenuItem
            onClick={() => {
              setEditing(menuField)
              setDialogOpen(true)
              closeMenu()
            }}
          >
            {t('common.edit', 'Редактировать')}
          </MenuItem>
          <MenuItem
            onClick={() => menuField && void handleDelete(menuField)}
            sx={{ color: 'error.main' }}
          >
            {t('common.delete', 'Удалить')}
          </MenuItem>
        </Menu>

        <StudentFormFieldDialog
          open={dialogOpen}
          field={editing}
          onClose={() => setDialogOpen(false)}
          onSaved={() => {
            setDialogOpen(false)
            void load()
          }}
        />
      </Box>
    </DashboardLayout>
  )
}
